import {getAllIncotermsWithPromiseLambda, getIncotermsWithPromiseLambda} from './functionSignatures';

const MAX_LENGTH_DESC = 28;

/**
 * Determines the incoterm code and description based on inco1, inco2, and allIncotermsData.
 *
 * @param {string} inco1
 * @param {string} inco2
 * @param {Array} allIncotermsData
 * @return {{code: string, description: string}}
 */
export const determineIncotermCodeAndDescription = (inco1, inco2, allIncotermsData) => {
    let code = '';
    let description = '';

    if (!inco1 && !inco2) {
        code = '';
        description = '';
    } else if (inco1 && inco2) {
        code = inco1;
        description = inco2;
    } else if (inco1 && !inco2) {
        const matchedIncoterm = allIncotermsData.find((item) => item.incoterms === inco1);
        if (matchedIncoterm) {
            code = matchedIncoterm.incoterms;
            description = matchedIncoterm.description;
        } else {
            code = inco1;
            description = '';
        }
    } else {
        code = '';
        description = '';
    }

    if (description && description.length > MAX_LENGTH_DESC) {
        description = description.substring(0, MAX_LENGTH_DESC);
    }

    return {code, description};
};

/**
 * Fetches and processes incoterms.
 *
 * @param {object} context
 * @param {object} getIncoterms
 * @param {object} getAllIncoterms
 * @param {string} vendorCode
 * @param {string} purchaseOrgValue
 * @param {LabelValue} incoterms
 * @return {Promise<{code: string, description: string}>}
 */
export const fetchAndProcessIncoterms = async ({
    context,
    getIncoterms,
    getAllIncoterms,
    vendorCode,
    purchaseOrgValue,
    incoterms = {},
}) => {
    // Fetch incoterms for vendor and purchase organization combination
    const incotermsPromise = getIncotermsWithPromiseLambda(
        context,
        getIncoterms,
        parseInt(vendorCode),
        purchaseOrgValue,
    );

    const allIncotermsPromise = getAllIncotermsWithPromiseLambda(context, getAllIncoterms);

    const [incotermsResponse, allIncotermsResponse] = await Promise.all([
        incotermsPromise,
        allIncotermsPromise,
    ]);

    const incotermsData = JSON.parse(incotermsResponse[0] || '[]');
    let inco1 = '';
    let inco2 = '';
    if (incoterms?.value || incoterms?.label) {
        if (incoterms?.value) {
            inco1 = incoterms.value;
        }
        if (incoterms?.label) {
            inco2 = incoterms.label;
        }
    } else if (incotermsData.length > 0) {
        inco1 = incotermsData[0].inco1 || '';
        inco2 = incotermsData[0].inco2 || '';
    }

    // Fetch all incoterms
    const allIncotermsData = JSON.parse(allIncotermsResponse[0] || '[]');

    const {code, description} = determineIncotermCodeAndDescription(
        inco1,
        inco2,
        allIncotermsData,
    );

    return {code, description};
};
